import {
  BlotterTable,
  Box,
  type CareOrder,
  type Column,
  type ColumnDef,
  Dialog,
  Divider,
  type FixingDetails,
  type FixingIndex,
  Flex,
  FormControlSizes,
  FormGroup,
  getDisplayNameOrNameLabel,
  HStack,
  Input,
  POSITIVE_CELL_CLASSNAME,
  SearchSelect,
  type SizeColumnParams,
  Text,
  toBig,
  toBigWithDefault,
  Toggle,
  useBlotterTable,
  useCallbackRef,
  type UseDisclosureWithContextReturn,
  WARNING_CELL_MED_CLASSNAME,
} from '@talos/kyoko';
import type { CellClassRules, GetContextMenuItemsParams, GridOptions } from 'ag-grid-community';
import { compact, values } from 'lodash-es';
import { useMemo, useState } from 'react';
import type { Observable } from 'rxjs';
import { useFixingIndices } from '../../../../providers/FixingIndexProvider';
import { getCareOrderBlotterEntityStatusColumn } from '../columns/CareOrderBlotterEntityStatusColumn';
import { useCareOrderBlotterAutoGroupColDef } from '../columns/useCareOrderBlotterAutoGroupColDef';
import { type CareOrderBlotterEntity, isCareOrderRow, isLegRow } from '../types';
import { useModifyFixingContextMenuItem } from '../useCareOrderMenu';
import { useValidateFixingDataObs } from './useValidateFixingDataObs';

type ValidateFixingDialogProps = UseDisclosureWithContextReturn<Observable<Map<string, CareOrderBlotterEntity>>>;

export const ValidateFixingDialog = ({
  context: careOrderBlotterEntitiesObs,
  ...dialogProps
}: ValidateFixingDialogProps) => {
  const [showAllDecimals, setShowAllDecimals] = useState(false);

  return (
    <Dialog
      {...dialogProps}
      title="Validate Fixing"
      headerSuffix={
        <Toggle
          size={FormControlSizes.Small}
          checked={showAllDecimals}
          onChange={setShowAllDecimals}
          label="Show All Decimals"
        />
      }
      width={1200}
      showClose
      showConfirm={false}
      cancelLabel="Close"
      closeOnClickOutside={false} // false so we don't close the validate fixing dialog when clicking in the modify fixing dialog, for example
    >
      {careOrderBlotterEntitiesObs && (
        <ValidateFixingDialogContent
          careOrderBlotterEntitiesObs={careOrderBlotterEntitiesObs}
          showAllDecimals={showAllDecimals}
        />
      )}
    </Dialog>
  );
};

export const ValidateFixingDialogContent = ({
  careOrderBlotterEntitiesObs,
  showAllDecimals,
}: {
  careOrderBlotterEntitiesObs: Observable<Map<string, CareOrderBlotterEntity>>;
  showAllDecimals: boolean;
}) => {
  const { fixingIndicesList } = useFixingIndices();

  const [fixingIndex, setFixingIndex] = useState<FixingIndex>();
  const [fixing, setFixing] = useState<string>('');

  const { getItem: getModifyFixingItem, dialogComponent: modifyFixingDialogComponent } =
    useModifyFixingContextMenuItem();
  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<CareOrderBlotterEntity>) => {
    return compact([getModifyFixingItem(params)]);
  });

  const autoGroupColumnDef = useCareOrderBlotterAutoGroupColDef();
  const gridOptions: GridOptions<CareOrderBlotterEntity> = useMemo(
    () => ({
      autoGroupColumnDef,
      treeData: true,
      getDataPath: item => item.dataPath,
      groupDefaultExpanded: -1, // all groups start fully expanded
    }),
    [autoGroupColumnDef]
  );

  const dataObservable = useValidateFixingDataObs({ careOrderBlotterEntitiesObs, fixingIndex });
  const columns = useColumns(fixingIndex?.Name, fixing);
  const blotterTableSettings = useMemo(
    () => ({
      showAllDecimals,
    }),
    [showAllDecimals]
  );
  const blotterTable = useBlotterTable({
    dataObservable,
    columns,
    errorObservable: undefined,

    rowID: 'rowID' satisfies keyof CareOrderBlotterEntity,
    gridOptions,
    contextMenuOptions: {
      getCustomContextMenuItems,
    },
    settings: blotterTableSettings,
  });

  return (
    <>
      <Flex flexDirection="column" h="500px" gap="spacing12" data-testid="validate-fixing-dialog-content">
        <Text justifySelf="flex-start" alignSelf="flex-start">
          Enter fixing details and manually validate against today&apos;s Care Orders below.
        </Text>
        <HStack gap="spacing8" w="100%" alignItems="flex-start" justifyContent="flex-start">
          <FormGroup label="Fixing Index" w="180px">
            <SearchSelect
              selection={fixingIndex}
              options={fixingIndicesList}
              showClear
              getLabel={getDisplayNameOrNameLabel}
              onChange={setFixingIndex}
              dataTestID="validate-fixing-fixing-index-selector"
            />
          </FormGroup>

          <FormGroup label="Fixing" w="180px">
            <Input
              value={fixing}
              onChange={e => setFixing(e.target.value)}
              data-testid="validate-fixing-fixing-input"
            />
          </FormGroup>
        </HStack>
        <Divider orientation="horizontal" />
        <Box flex="1">
          <BlotterTable {...blotterTable} background="backgroundModal" />
        </Box>
      </Flex>
      {modifyFixingDialogComponent}
    </>
  );
};

const useColumns = (index: string | undefined, fixing: string | undefined): Column[] => {
  const fixingCellClassRules: CellClassRules<CareOrderBlotterEntity, unknown> = useMemo(() => {
    const fixingBig = toBig(fixing);
    return {
      [POSITIVE_CELL_CLASSNAME]: params => {
        if (!fixingBig || !index) {
          return false;
        }

        if (!params.data || isCareOrderRow(params.data) || isLegRow(params.data)) {
          return false;
        }

        // Apply the positive class name if we are a match
        return fixingBig.eq(toBigWithDefault(getFixingForIndex(params.data.FixingDetails, index), 0));
      },
      [WARNING_CELL_MED_CLASSNAME]: params => {
        if (!fixingBig || !index) {
          return false;
        }

        if (!params.data || isCareOrderRow(params.data) || isLegRow(params.data)) {
          return false;
        }

        // Apply the warning class name if we are not matching
        return !fixingBig.eq(toBigWithDefault(getFixingForIndex(params.data.FixingDetails, index), 0));
      },
    };
  }, [index, fixing]);

  return useMemo(
    () =>
      [
        {
          type: 'security',
          field: 'Symbol',
          title: 'Symbol',
        },
        {
          type: 'side',
          field: 'tradedSideOrSide',
          title: 'Side',
        },
        {
          type: 'size',
          field: 'OrderQty',
          title: 'Order Qty',
          initialWidth: 120,
          params: {
            currencyField: 'Currency',
          } satisfies SizeColumnParams<CareOrder>,
        },
        {
          type: 'size',
          field: 'filledQty',
          title: 'Filled Qty',
          initialWidth: 120,
          params: {
            currencyField: 'Currency',
          } satisfies SizeColumnParams<CareOrderBlotterEntity>,
        },
        getCareOrderBlotterEntityStatusColumn({ initialPinned: undefined }),
        {
          type: 'date',
          field: 'SubmitTime',
          title: 'Submit Time',
          width: 130, // change to initialWidth once submit time no longer hard-codes width
          initialSort: '-',
        },
        {
          type: 'text',
          field: 'Group',
          title: 'Group',
          initialWidth: 110,
        },
        {
          field: 'fixingIndices',
          title: 'Fixing Index',
          id: 'fixingIndices',
          type: 'fixingIndices',
          initialWidth: 100,
        },
        {
          field: 'FixingDetails',
          title: 'Fixing Price',
          type: 'fixingPrice',
          id: 'fixingPrice',
          initialWidth: 160,
          cellClassRules: fixingCellClassRules,
        },
      ] satisfies ColumnDef<CareOrderBlotterEntity>[],
    [fixingCellClassRules]
  );
};

function getFixingForIndex(fixingDetails: FixingDetails | undefined, index: string): string | undefined {
  if (fixingDetails?.Index === index) {
    return fixingDetails.Fixing;
  }

  if (fixingDetails?.MultilegFixingDetails) {
    return values(fixingDetails.MultilegFixingDetails).find(value => value.Index === index)?.Fixing;
  }

  return undefined;
}
